// CHG-131 REQ-CP-025: ProfileModal 본체
// 데이터: usePublicProfile(userId)
// 본인 클릭은 ProfileModalProvider에서 /profile redirect로 처리 — 본 컴포넌트는 타인 전용
import { useEffect } from 'react';
import { X } from 'lucide-react';
import type { ProfileColorKey } from '@maios/shared';
import { usePublicProfile } from '../../hooks/useMyProfile';
import Avatar from './Avatar';
import UserDisplayName from './UserDisplayName';
import Spinner from '../ui/Spinner';

interface Props {
  userId: string;
  onClose: () => void;
}

export default function ProfileModal({ userId, onClose }: Props) {
  const { data: profile, isLoading, error } = usePublicProfile(userId);

  // ESC 닫기
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // 다른 조직 사용자: GET 404 → AppError 응답
  const is404 = !!(error && (error as { response?: { status?: number } }).response?.status === 404);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="profile-modal-title"
    >
      <div
        className="bg-white rounded-xl shadow-xl w-[400px] max-w-[95vw] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 닫기 버튼 */}
        <div className="flex items-center justify-end px-3 pt-3 -mb-2">
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="닫기"
          >
            <X className="size-5" />
          </button>
        </div>

        {isLoading ? (
          <div className="px-6 py-10 flex justify-center">
            <Spinner className="size-8" />
          </div>
        ) : is404 ? (
          <div className="px-6 py-8 text-center">
            <p id="profile-modal-title" className="text-sm font-medium text-gray-900 mb-1">
              프로필을 볼 수 없습니다
            </p>
            <p className="text-sm text-gray-500">이 사용자의 프로필을 볼 수 없습니다.</p>
          </div>
        ) : profile ? (
          <div className="px-6 pb-6">
            {/* 큰 아바타 */}
            <div className="flex flex-col items-center mb-4">
              <div className="size-24 mb-3">
                <Avatar
                  userId={profile.userId}
                  name={profile.name}
                  firstName={profile.firstName}
                  avatarUrl={profile.avatarUrl}
                  displayColor={profile.displayColor as ProfileColorKey | null | undefined}
                  size="lg"
                  className="size-24 text-2xl"
                />
              </div>
              <h2 id="profile-modal-title" className="text-center">
                <UserDisplayName
                  userId={profile.userId}
                  name={profile.name}
                  nickname={profile.nickname}
                  displayColor={profile.displayColor as ProfileColorKey | null | undefined}
                  bold
                  showFullForm
                  className="text-lg"
                />
              </h2>
              {/* 직책 · 직급 (공개 시만) */}
              {(profile.jobTitle || profile.jobRank) && (
                <p className="text-sm text-gray-600 mt-1">
                  {[profile.jobTitle, profile.jobRank].filter(Boolean).join(' · ')}
                </p>
              )}
              <p className="text-sm text-gray-500 mt-1">{profile.email}</p>
            </div>

            {/* 추가 공개 필드 — 행 단위. 비공개 또는 값 없음이면 행 자체 미렌더. */}
            {(profile.birthday || profile.phone || profile.memo) && (
              <div className="border-t border-gray-100 pt-4 space-y-2">
                {profile.birthday && (
                  <ProfileRow label="생일" value={profile.birthday} />
                )}
                {profile.phone && (
                  <ProfileRow label="전화" value={profile.phone} />
                )}
                {profile.memo && (
                  <ProfileRow label="메모" value={profile.memo} multiline />
                )}
              </div>
            )}

            {/* 닫기 */}
            <div className="mt-6">
              <button
                type="button"
                onClick={onClose}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
              >
                닫기
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function ProfileRow({ label, value, multiline }: { label: string; value: string; multiline?: boolean }) {
  return (
    <div className={multiline ? 'flex flex-col gap-1' : 'flex items-start gap-3'}>
      <span className="text-sm font-medium text-gray-500 shrink-0 w-12">{label}</span>
      <span className={`text-sm text-gray-800 ${multiline ? 'whitespace-pre-wrap break-words' : 'flex-1'}`}>{value}</span>
    </div>
  );
}
